/**
 * Card, Section, KeyValue, Divider, Callout, Note, Inset, Quote.
 *
 * The console is panels bounded by a 1px hairline. Cards never nest more than one deep and
 * never carry a real shadow — the resting shadow token is 5% alpha and exists only to lift
 * a panel off the canvas by a hair. Anything with a visible shadow is floating: a modal, a
 * drawer, a toast, a menu.
 *
 * A `Section` is the unboxed alternative: a small heading line with content beneath it and
 * no border. Use it when the grouping is obvious from the page and a panel would just be
 * another rectangle.
 */
import type { ReactElement, ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';
import { HelpTip } from './HelpTip.tsx';

export type CardProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Renders a "?" beside the title, resolved from /api/help (doctrine 8). */
  helpKey?: string;
  /** Rendered between the title and the actions: status tags, ids, elapsed times. */
  meta?: ReactNode;
  /** A leading status dot in the header. */
  dot?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  /** 'none' for a card whose body is a table; 'tight' for dense lists. */
  padding?: 'normal' | 'tight' | 'none';
  /** Clips the body to the card radius. Set when the body is a table. */
  flush?: boolean;
  className?: string;
  id?: string;
  children: ReactNode;
};

export function Card({
  title,
  subtitle,
  helpKey,
  meta,
  dot,
  actions,
  footer,
  padding = 'normal',
  flush = false,
  className,
  id,
  children,
}: CardProps): ReactElement {
  const hasHeader = Boolean(title || actions || meta || dot);
  return (
    <section
      className={['nd-card', className].filter(Boolean).join(' ')}
      data-flush={flush ? 'true' : undefined}
      id={id}
    >
      {hasHeader ? (
        <header className="nd-card-head">
          {dot}
          {title ? (
            <h2 className="nd-card-title">
              <span className="nd-truncate">{title}</span>
              {helpKey ? <HelpTip helpKey={helpKey} /> : null}
            </h2>
          ) : null}
          {subtitle ? <span className="nd-card-sub">{subtitle}</span> : null}
          {meta}
          {actions ? <div className="nd-card-actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="nd-card-body" data-pad={padding === 'normal' ? undefined : padding}>
        {children}
      </div>
      {footer ? <footer className="nd-card-foot">{footer}</footer> : null}
    </section>
  );
}

export type SectionProps = {
  title?: ReactNode;
  helpKey?: string;
  /** Sits immediately after the title — a tag, a clock, a scope. */
  meta?: ReactNode;
  /** Right-aligned: usually one LinkAction into the detail behind the section. */
  actions?: ReactNode;
  className?: string;
  id?: string;
  children: ReactNode;
};

/** A heading line and its content, with no panel around either. */
export function Section({ title, helpKey, meta, actions, className, id, children }: SectionProps): ReactElement {
  return (
    <section className={['nd-section', className].filter(Boolean).join(' ')} id={id}>
      {title || meta || actions ? (
        <div className="nd-section-line">
          {title ? <h2>{title}</h2> : null}
          {helpKey ? <HelpTip helpKey={helpKey} /> : null}
          {meta}
          {actions ? <span className="nd-right">{actions}</span> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export type KeyValueRow = { label: ReactNode; value: ReactNode; key?: string };

/** The definition list used in every drawer and confirmation. Labels left, values right. */
export function KeyValue({ rows, className }: { rows: KeyValueRow[]; className?: string }): ReactElement {
  return (
    <dl className={['nd-kv', className].filter(Boolean).join(' ')}>
      {rows.map((row, index) => (
        <div key={row.key ?? index} style={{ display: 'contents' }}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Divider({ accent = false, className }: { accent?: boolean; className?: string } = {}): ReactElement {
  return <div className={['nd-divider', className].filter(Boolean).join(' ')} data-accent={accent ? 'true' : undefined} />;
}

/**
 * 'gated' and 'ai' render identically (orchid) and both mean "the assistant wrote, or would
 * have written, something here". Two names because the callers mean different things by it:
 * Badge and RecordField say `gated` about a policy hold, transcripts say `ai` about a machine
 * turn. Keeping both spellings costs one union member and stops a screen having to remember
 * which synonym this particular component accepts — which is exactly how a screen ends up
 * hard-coding a colour instead.
 */
export type CalloutTone = 'neutral' | 'danger' | 'warn' | 'ok' | 'info' | 'ai' | 'gated' | 'rule';

/**
 * A bordered statement of consequence. The one in a ConfirmDialog says what is about to
 * happen; the one on the outage record says why the assistant cannot fill in an ETA.
 * It is prose ON PURPOSE and it is the only place prose belongs — everything else on a
 * record is typed fields.
 *
 * `rule` is the tone for a PRODUCT CONSTRAINT — "there is no transfer or bridge control
 * here, on purpose". It exists because those callouts were being written as `ok`, and green
 * in this console means "a thing in the world is fine". A rule is not a success; it renders
 * as the locked idiom (dashed hairline, padlock ink) so it matches the locked buttons, the
 * locked toggles and the locked ETA field that state the same promise elsewhere.
 */
export function Callout({
  tone = 'neutral',
  icon,
  children,
  className,
}: {
  tone?: CalloutTone;
  icon?: IconName;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <div
      className={['nd-callout', className].filter(Boolean).join(' ')}
      data-tone={tone === 'neutral' ? undefined : tone === 'gated' ? 'ai' : tone}
    >
      {icon ? <Icon name={icon} size={16} /> : tone === 'rule' ? <Icon name="lock" size={16} /> : null}
      <span>{children}</span>
    </div>
  );
}

/** The quiet footnote under a control: a rule, a limit, a demo-mode caveat. */
export function Note({ icon = 'lock', children }: { icon?: IconName; children: ReactNode }): ReactElement {
  return (
    <div className="nd-modal-note">
      <Icon name={icon} size={13} />
      <span>{children}</span>
    </div>
  );
}

/** An inset panel inside a card — the ETA box on the outage record. */
export function Inset({ children, className }: { children: ReactNode; className?: string }): ReactElement {
  return <div className={['nd-inset', className].filter(Boolean).join(' ')}>{children}</div>;
}

/**
 * Someone's actual words. Bordered on the left in the critical hue because in this product
 * a verbatim quote is almost always a caller describing something that is wrong.
 */
export function Quote({ children, footer }: { children: ReactNode; footer?: ReactNode }): ReactElement {
  return (
    <blockquote className="nd-quote">
      {children}
      {footer ? (
        <div className="nd-row" style={{ marginTop: 9, gap: 6 }}>
          {footer}
        </div>
      ) : null}
    </blockquote>
  );
}

export { PageHeader } from './PageHeader.tsx';
export type { PageHeaderProps } from './PageHeader.tsx';

export default Card;
